import { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/api';

interface PromptVersion {
  versionNumber: number;
  template: string;
  createdAt: string;
}

interface PromptDoc {
  _id: string;
  key: string;
  name: string;
  description?: string;
  template: string;
  variables: string[];
  scopeType: string;
  currentVersion: number;
  versions: PromptVersion[];
  updatedAt: string;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface PromptCardProps {
  prompt: PromptDoc;
  onUpdated: (updated: PromptDoc) => void;
}

function PromptCard({ prompt, onUpdated }: PromptCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(prompt.template);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const saveMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditText(prompt.template);
  }, [prompt.template]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowVersionDropdown(false);
      }
      if (saveMenuRef.current && !saveMenuRef.current.contains(e.target as Node)) {
        setShowSaveMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEditClick = () => {
    setIsExpanded(true);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(prompt.template);
    setIsExpanded(false);
  };

  const handleSave = async (saveAsNew: boolean) => {
    setSaving(true);
    setShowSaveMenu(false);
    try {
      const res = await adminApi.updatePrompt(prompt._id, { template: editText, saveAsNew });
      onUpdated(res.data.data);
      setIsEditing(false);
      setIsExpanded(false);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreVersion = async (versionNumber: number) => {
    setRestoring(true);
    try {
      const res = await adminApi.restorePromptVersion(prompt._id, versionNumber);
      onUpdated(res.data.data);
      setShowVersionDropdown(false);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to restore');
    } finally {
      setRestoring(false);
    }
  };

  const toggleVersionExpand = (vn: number) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(vn)) next.delete(vn);
      else next.add(vn);
      return next;
    });
  };

  const versions = prompt.versions ?? [];
  const currentVersion = prompt.currentVersion ?? 1;
  const hasMultipleVersions = versions.length > 1;
  const isV1Current = currentVersion === 1;

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* Card header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <svg
                  className={`w-5 h-5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <h3 className="font-semibold text-gray-900 truncate">{prompt.name}</h3>
            </div>

            {prompt.description && (
              <p className="text-sm text-gray-500 mt-1 ml-7">{prompt.description}</p>
            )}

            <div className="flex items-center gap-3 mt-2 ml-7 flex-wrap">
              <span className="text-xs text-gray-500">
                v{currentVersion} · Last edited {formatRelativeTime(prompt.updatedAt)}
              </span>

              {hasMultipleVersions && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View history ({versions.length} {versions.length === 1 ? 'version' : 'versions'})
                  </button>

                  {showVersionDropdown && (
                    <div className="absolute left-0 top-full mt-1 w-[560px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[500px] overflow-y-auto">
                      <div className="p-3 border-b border-gray-100 bg-gray-50 sticky top-0">
                        <h4 className="font-semibold text-sm text-gray-900">Version History</h4>
                      </div>
                      <div className="p-2 space-y-1">
                        {[...versions].reverse().map((version) => {
                          const isCurrent = version.versionNumber === currentVersion;
                          const isVersionExpanded = expandedVersions.has(version.versionNumber);
                          return (
                            <div
                              key={version.versionNumber}
                              className={`rounded-md border ${isCurrent ? 'bg-blue-50 border-blue-200' : 'border-gray-200'}`}
                            >
                              <div className="p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm text-gray-900">
                                        Version {version.versionNumber}
                                      </span>
                                      {isCurrent && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                          Current
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {formatRelativeTime(version.createdAt)}
                                    </p>
                                    {!isVersionExpanded && (
                                      <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">
                                        {version.template.substring(0, 120)}...
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                      onClick={() => toggleVersionExpand(version.versionNumber)}
                                      className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                                    >
                                      {isVersionExpanded ? 'Hide' : 'View'}
                                    </button>
                                    {!isCurrent && (
                                      <button
                                        onClick={() => handleRestoreVersion(version.versionNumber)}
                                        disabled={restoring}
                                        className="px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md disabled:opacity-50"
                                      >
                                        Restore
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {isVersionExpanded && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="bg-gray-50 rounded-md p-3 max-h-56 overflow-y-auto">
                                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                                        {version.template}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleEditClick}
            className="flex-shrink-0 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Collapsible body */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {!isEditing ? (
            <div className="bg-white rounded-md border border-gray-200 overflow-auto max-h-[400px]">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono p-4">
                {prompt.template}
              </pre>
            </div>
          ) : (
            <div className="space-y-3">
              {isV1Current && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  Version 1 is read-only. Your changes will be saved as a new version.
                </div>
              )}
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full min-h-[200px] max-h-[500px] p-4 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y bg-white"
                placeholder="Enter prompt text..."
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                >
                  Cancel
                </button>
                <div ref={saveMenuRef} className="relative">
                  <button
                    onClick={() => isV1Current ? handleSave(true) : setShowSaveMenu(!showSaveMenu)}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center gap-1 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                    {!isV1Current && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>

                  {showSaveMenu && !isV1Current && (
                    <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <div className="py-1">
                        <button
                          onClick={() => handleSave(false)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <div className="font-medium">Save changes</div>
                          <div className="text-xs text-gray-500 mt-0.5">Update current version</div>
                        </button>
                        <button
                          onClick={() => handleSave(true)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                        >
                          <div className="font-medium">Save as new version</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Create v{versions.length + 1}
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PromptManagement() {
  const [prompts, setPrompts] = useState<PromptDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listPrompts({ limit: 200, scopeType: 'global' });
      setPrompts(res.data.data?.prompts ?? []);
    } catch {
      setError('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await adminApi.syncPrompts();
      await loadPrompts();
    } catch {
      alert('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handlePromptUpdated = (updated: PromptDoc) => {
    setPrompts(prev => prev.map(p => (p._id === updated._id ? updated : p)));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Prompt Templates</h1>
            <p className="text-sm text-gray-500 mt-1">
              Edit prompts used by AI flows. Version 1 of each prompt is read-only.
            </p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync from registry'}
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Prompt templates</h2>
          <p className="text-sm text-gray-500 mb-5">
            Edit the prompt text used for each AI feature. Use placeholders where needed
            (e.g. <code className="bg-gray-100 px-1 rounded text-xs">{'{{userContext}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{userMessage}}'}</code>).
          </p>

          {loading && (
            <div className="text-sm text-gray-500 text-center py-8">Loading prompts…</div>
          )}
          {error && (
            <div className="text-sm text-red-600 text-center py-8">{error}</div>
          )}
          {!loading && !error && prompts.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-8">
              No prompts found.{' '}
              <button onClick={handleSync} className="text-blue-600 hover:underline">
                Sync from registry
              </button>
            </div>
          )}

          {!loading && !error && prompts.length > 0 && (
            <div className="space-y-3">
              {prompts.map(prompt => (
                <PromptCard
                  key={prompt._id}
                  prompt={prompt}
                  onUpdated={handlePromptUpdated}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
